/**
 * RYFE — Whiteboard application shell + router (Block 2).
 * The anonymous shared-board view is handled before the session provider (it
 * needs no auth). Everything else lives behind a SINGLE auth hull (RequireAuth):
 * the auth check is done once here, not per view, so no protected route can be
 * left open by omission.
 */

import { RyfeProvider, useRyfe } from "./RyfeContext";
import { navigate, useRoute } from "./router";
import { RyfeAccount } from "./components/RyfeAccount";
import { RyfeBoard } from "./components/RyfeBoard";
import { RyfeMarkdown } from "./components/RyfeMarkdown";
import { RyfeList } from "./components/RyfeList";
import { RyfeFiles } from "./components/RyfeFiles";
import { RyfeForm } from "./components/RyfeForm";
import { RyfeFavorites } from "./components/RyfeFavorites";
import { RyfeGroup } from "./components/RyfeGroup";
import { RyfeGroupSettings } from "./components/RyfeGroupSettings";
import { RyfeHome } from "./components/RyfeHome";
import { RyfePrivate } from "./components/RyfePrivate";
import { RyfeSystemLists } from "./components/RyfeSystemLists";
import { RyfeRecentFolders } from "./components/RyfeRecentFolders";
import { RyfeRoleFolderView } from "./components/RyfeRoleFolderView";
import { RyfeLibrary } from "./components/RyfeLibrary";
import { RyfeLogin } from "./components/RyfeLogin";
import { RyfeKiChat } from "./components/RyfeKiChat";
import { RyfeKiKnowledge } from "./components/RyfeKiKnowledge";
import { RyfeLenses } from "./components/RyfeLenses";
import { RyfeMcpTokens } from "./components/RyfeMcpTokens";
import { RyfeOAuthConsent } from "./components/RyfeOAuthConsent";
import { RyfeSettings } from "./components/RyfeSettings";
import { RyfeWorkspaceSettings } from "./components/RyfeWorkspaceSettings";
import { RyfeShares } from "./components/RyfeShares";
import { RyfeShared } from "./components/RyfeShared";
import { RyfeSharedWithMe } from "./components/RyfeSharedWithMe";
import { RyfeShell } from "./components/RyfeShell";

import "./components/ryfe.scss";

const NotFound = () => (
  <div className="ryfe-page">
    <h1 className="ryfe-page__title">Nicht gefunden</h1>
    <p className="ryfe-muted">Diese Seite existiert nicht.</p>
    <button
      className="ryfe-btn ryfe-btn--primary"
      onClick={() => navigate("/files")}
    >
      Zu „Meine Dateien“
    </button>
  </div>
);

/** The single auth hull + route switch. Rendered inside RyfeProvider. */
const RoutedApp = () => {
  const route = useRoute();
  const { status } = useRyfe();

  if (status === "loading") {
    return <div className="ryfe-muted">Loading…</div>;
  }
  if (status === "anon") {
    // One gate for every protected route (RequireAuth). The URL is preserved,
    // so after login the user lands on the route they deep-linked to.
    return <RyfeLogin />;
  }

  // Board editor is full-screen, outside the management shell.
  // v1.3.0 Block 4: die Notiz öffnet als eigene Seite, wie das Board.
  if (route.name === "md") {
    return <RyfeMarkdown key={route.params.id} fileId={route.params.id} />;
  }
  if (route.name === "board") {
    return <RyfeBoard key={route.params.id} fileId={route.params.id} />;
  }
  // CC_05 Block 8: eine Liste öffnet als eigene Seite (nur Tabellen-Ansicht).
  if (route.name === "list") {
    return <RyfeList key={route.params.id} fileId={route.params.id} />;
  }

  let content;
  switch (route.name) {
    case "files":
      content = <RyfeFiles folderId={route.params.folderId || undefined} />;
      break;
    case "library":
      content = <RyfeLibrary folderId={route.params.folderId || undefined} />;
      break;
    case "mcp-tokens":
      content = <RyfeMcpTokens />;
      break;
    // v1.6 / D-KI (AP2) — KI-Chat an Dify (Phase-1-Einstieg).
    case "ki-chat":
      content = <RyfeKiChat />;
      break;
    // D-121c — editierbare KI-Ordner-Wissens-Seiten (je Intent + Sessions).
    case "ki-knowledge":
      content = <RyfeKiKnowledge />;
      break;
    // v1.6 / D-KI (AP3) — Systemliste „Lenses".
    case "lenses":
      content = <RyfeLenses />;
      break;
    // R4 (Punkt 2) — OAuth-Consent für den MCP-Connector.
    case "oauth-consent":
      content = <RyfeOAuthConsent />;
      break;
    case "shared-with-me":
      content = <RyfeSharedWithMe />;
      break;
    // v1.8 AP-5 — „Dateien"-Unterpunkte: Favoriten (P21), Zuletzt (P23),
    // Archiv/Papierkorb (P13, per Scope).
    case "favorites":
      content = <RyfeFavorites />;
      break;
    case "recent":
      content = <RyfeRecentFolders />;
      break;
    case "role-folder":
      content = (
        <RyfeRoleFolderView
          key={route.params.role}
          role={route.params.role as "trash" | "archive"}
        />
      );
      break;
    // Revision R3 — zentrale Freigaben-Verwaltung.
    case "shares":
      content = <RyfeShares />;
      break;
    case "account":
      content = <RyfeAccount />;
      break;
    // CC_11 (D-72) — "Meine Einstellungen": per-user global settings.
    case "settings":
      content = <RyfeSettings />;
      break;
    case "workspace-settings":
      content = <RyfeWorkspaceSettings />;
      break;
    case "home":
      content = <RyfeHome />;
      break;
    // v1.8 Cluster B.5 (D-146) — Privatbereich (Cross-WS-Überblick).
    case "private":
      content = <RyfePrivate />;
      break;
    // Systemlisten-Überblick des aktiven Workspace (D-158/D-159, Weg 2).
    case "systemlists":
      content = <RyfeSystemLists />;
      break;
    // v1.8 WS-Plattform (D-132) — Gruppen-Dashboard (A4) + Einstellungen (A5).
    case "group":
      content = (
        <RyfeGroup key={route.params.groupId} groupId={route.params.groupId} />
      );
      break;
    case "group-settings":
      content = (
        <RyfeGroupSettings
          key={route.params.groupId}
          groupId={route.params.groupId}
        />
      );
      break;
    default:
      content = <NotFound />;
  }
  return <RyfeShell>{content}</RyfeShell>;
};

export const RyfeApp = () => {
  // Views outside the auth hull: the anonymous share view and the form page
  // (the form itself decides public vs auth server-side, D-66 — the session
  // cookie still travels, so an auth-gated form works for signed-in users).
  // WICHTIG: `useRoute` (nicht das einmalige `matchRoute`) — sonst reagiert dieser
  // äußere Wrapper NICHT auf client-seitige Navigation (History pushState). Ein
  // In-App-Klick auf einen Formular-Link (z. B. „Formulare (Gruppe)") landete
  // sonst im Auth-Switch, der kein „form" kennt → „Nicht gefunden". Nur ein
  // voller Reload funktionierte. Mit useRoute rendert der Wrapper bei jeder
  // Navigation neu und greift den form-/share-Zweig korrekt ab.
  const route = useRoute();
  if (route.name === "share") {
    return <RyfeShared />;
  }
  if (route.name === "form") {
    return <RyfeForm formId={route.params.formId} />;
  }
  return (
    <RyfeProvider>
      <RoutedApp />
    </RyfeProvider>
  );
};
